<template>
  <div class="wwf-form light">
    <!-- HEAD -->
    <h3>Connect with our team</h3>

    <p class="p-presentation">
      Get in touch with Christine if you have any questions about gifts in
      Wills, or to request an information booklet.
    </p>

    <!-- INPUTS -->
    <input
      name="firstName"
      type="text"
      v-model="firstName"
      v-validate="{ required: true }"
      :class="{ 'border-red': errors.has('firstName') }"
      placeholder="First name"
      autocomplete="given-name"
    />

    <input
      name="lastName"
      type="text"
      v-model="lastName"
      v-validate="{ required: true }"
      :class="{ 'border-red': errors.has('lastName') }"
      placeholder="Last name"
      autocomplete="family-name"
    />

    <div class="small-12 medium-12 with-input-loading">
      <input
        name="email"
        type="email"
        v-model="email"
        v-validate="{ required: true, email: true, bequestCheckEmail: true }"
        :class="{
          'border-red': errors.has('email') || errors.has('bequestCheckEmail'),
        }"
        placeholder="Email"
        autocomplete="email"
      />
      <div class="input-loading" v-if="checkEmailLoading">
        <img src="https://storage.googleapis.com/fire-panda.appspot.com/website-public-assets/1442/dist/assets/img/loading-tail-spin.svg" />
      </div>
      <span class="wwf-alert">
        <p v-if="errors.firstByRule('email', 'bequestCheckEmail')">
          {{ errors.first("email") }}
        </p>
      </span>
    </div>

    <div class="small-12 medium-12 with-input-loading">
      <input
        name="phone"
        type="tel"
        v-model="phone"
        v-validate="{ required: true, bequestCheckPhone: true }"
        :class="{
          'border-red': errors.has('phone') || errors.has('bequestCheckPhone'),
        }"
        placeholder="Phone"
        autocomplete="phone"
      />
      <div class="input-loading" v-if="phoneCheck.checkPhoneLoading">
        <img src="https://storage.googleapis.com/fire-panda.appspot.com/website-public-assets/1442/dist/assets/img/loading-tail-spin.svg" />
      </div>
      <span class="wwf-alert">
        <p v-if="errors.firstByRule('phone', 'bequestCheckPhone')">
          {{ errors.first("phone") }}
        </p>
      </span>
    </div>

    <input
      name="postcode"
      type="text"
      v-model="postcode"
      v-validate="{ required: true }"
      :class="{ 'border-red': errors.has('postcode') }"
      placeholder="Postcode"
      autocomplete="postal-code"
    />

    <!-- DISCLAMER -->
    <div class="privacy-statement">
      <p>
        By signing you accept
        <a href="/privacy-policy" target="_blank"
          >WWF-Australia&rsquo;s Privacy Policy.</a
        >
      </p>
    </div>

    <!-- SUBMIT -->
    <input
      type="button"
      class="btn-submit"
      v-on:click="save()"
      :disabled="isSaving"
      :value="isSaving ? 'Loading...' : 'Submit'"
    />

    <!-- MANAGER -->
    <div class="text-center">
      <ul>
        <li id="round-thumb">
          <img
            style="border-radius: 50%; max-width: 65%"
            src="https://storage.googleapis.com/fire-panda.appspot.com/website-public-assets/1442/dist/assets/img/img-thumb-christine-robinson-200x200.jpg"
            alt="Christine Robinson profile photo © WWF-Aus / Fiora Sacco"
          />Christine Robinson, Manager, Living Planet Legacy Society
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { Validator } from "vee-validate";

export default {
  name: "bequest",
  data: function () {
    return {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      postcode: "",
      isSaving: false,
      timeout: null,
      lastTimestamp: Date.now(),
      checkEmailLoading: false,
      emailRegex: /.+\@.+\..+/,
      phoneCheck: {
        timeout: null,
        lastTimestamp: Date.now(),
        checkPhoneLoading: false,
      },
    };
  },

  created() {
    // CHECK EMAIL
    const bequestCheckEmail = (email) => {
      if (email.length > 0 && this.emailRegex.test(email)) {
        return new Promise((resolve, reject) => {
          let timestamp = Date.now(); // timestamp for this api call
          this.lastTimestamp = timestamp; // 1 , 2 , 3
          this.checkEmailLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validateEmail", email)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                this.checkEmailLoading = false;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.checkEmailLoading = false;
              // If the call failed, do not keep the user from donating
              resolve({
                valid: true,
              });
            });
        });
      } else {
        return {
          valid: false,
          data: { message: "Email invalid" },
        };
      }
    };

    Validator.extend("bequestCheckEmail", {
      validate: bequestCheckEmail,
      getMessage: (field, params, data) => data.message,
    });

    // CHECK PHONE NUMBER
    const bequestCheckPhone = (phone) => {
      if (phone.length > 0) {
        return new Promise((resolve, reject) => {
          let timestamp = Date.now(); // timestamp for this api call
          this.phoneCheck.lastTimestamp = timestamp; // 1 , 2 , 3
          this.phoneCheck.checkPhoneLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validatePhone", phone)
            .then((response) => {
              if (response && timestamp >= this.phoneCheck.lastTimestamp) {
                this.phoneCheck.checkPhoneLoading = false;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.phoneCheck.checkPhoneLoading = false;
              // If the call failed, do not keep the user from donating
              resolve({
                valid: true,
              });
            });
        });
      } else {
        return {
          valid: false,
          data: { message: "Invalid phone number, try again" },
        };
      }
    };

    Validator.extend("bequestCheckPhone", {
      validate: bequestCheckPhone,
      getMessage: (field, params, data) => data.message,
    });
  },

  methods: {
    save: function () {
      // Exit the function if it's busy
      if (this.isSaving) {
        return;
      }

      this.$validator.validateAll().then(async (result) => {
        try {
          if (!result) {
            return;
          }
          this.isSaving = true;

          const response = await Vue.http.post(
            wwfconfigs.CLOUDFUNCTION + "/saveBequest",
            {
              firstName: this.firstName,
              lastName: this.lastName,
              email: this.email,
              postcode: this.postcode,
              phone: this.phone,
            }
          );

          this.isSaving = false;

          // Redirect to thank you page
          window.location.href =
            "/donate/give-in-your-will/gifts-in-will-thank-you";
        } catch (err) {
          console.error(err);
        } finally {
          // Reset form
          this.firstName = "";
          this.lastName = "";
          this.email = "";
          this.postcode = "";
          this.phone = "";

          this.isSaving = false;
        }
      });
    },
  },
};
</script>

<style scoped>
p.p-presentation {
  margin-bottom: 24px;
}

.btn-submit {
  width: 100%;
  margin-bottom: 24px;
}

.input-loading {
  top: 6px;
}
</style>


// WEBPACK FOOTER //
// bequest.vue?413ca5d0